import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LoadingSpinner, { LoadingGalleryCard } from '../components/LoadingSpinner';
import { ErrorCard } from '../components/ErrorMessage';
import { useAPI } from '../hooks/useAPI';
import { galleryAPI } from '../services/api';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // API call pour récupérer la galerie
  const { 
    data: gallery, 
    loading, 
    error,
    refetch 
  } = useAPI(() => galleryAPI.getAll());

  const openModal = (item) => {
    setSelectedImage(item);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Galerie de Nos Réalisations
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Découvrez la transformation spectaculaire de nos clients grâce à nos services de detailing professionnel
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <LoadingGalleryCard key={index} />
              ))}
            </div>
          ) : error ? (
            <ErrorCard error={error} onRetry={refetch} />
          ) : gallery && gallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gallery.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300">
                  <div className="relative">
                    <div className="grid grid-cols-2 gap-0">
                      {/* Avant */}
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.beforeImage} 
                          alt={`Avant - ${item.description}`}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <Badge variant="secondary" className="bg-red-100 text-red-800">
                            Avant
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Après */}
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.afterImage} 
                          alt={`Après - ${item.description}`}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-2 right-2">
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Après
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                      <Button
                        onClick={() => openModal(item)}
                        className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 bg-white text-gray-900 hover:bg-gray-100"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir en détail
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-blue-100 text-blue-800">{item.service}</Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{item.description}</h3>
                    <p className="text-sm text-gray-600">
                      Transformation réalisée dans nos ateliers professionnels
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                Aucune réalisation disponible pour le moment
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
          <div className="relative bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <Button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-gray-900 text-white hover:bg-gray-800 rounded-full p-2"
            >
              <X className="w-5 h-5" />
            </Button>
            
            <div className="p-6">
              <div className="mb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2">{selectedImage.service}</Badge>
                <h3 className="text-2xl font-bold text-gray-900">{selectedImage.description}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-red-600">Avant</h4>
                  <img 
                    src={selectedImage.beforeImage} 
                    alt={`Avant - ${selectedImage.description}`}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-green-600">Après</h4>
                  <img 
                    src={selectedImage.afterImage} 
                    alt={`Après - ${selectedImage.description}`}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600 mb-4">
                  Résultat obtenu grâce à nos techniques professionnelles et notre matériel de pointe
                </p>
                <Link to="/reservation">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Réserver le même service
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Votre véhicule mérite le même traitement
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Faites confiance à AutoSpa 77 pour redonner tout son éclat à votre automobile
          </p>
          <Link to="/reservation">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg">
              Réserver maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;